import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import type { ComponentProps, ReactNode } from "react";
import { cn } from "cn";
import { Button } from "./button";
import { Kbd } from "./kbd";

/** Base UI accepts a class name or a function of the part's state; both have to survive merging. */
function merge<State>(
  base: string,
  className: string | ((state: State) => string | undefined) | undefined,
) {
  return (state: State) => cn(base, typeof className === "function" ? className(state) : className);
}

/**
 * The whole modal surface in one part: the scrim and the panel above it, both inside the portal.
 * Deliberately not separable — a modal without a scrim is a panel floating over a page that still
 * looks usable, and no caller should be able to ship that by leaving a part out.
 *
 * Width and any optical offset are the caller's — one board's nudge off centre is positioning, not
 * a fact about dialogs. Elevation is not: a modal lifts off the scrim the same way a menu lifts
 * off the page, so the shadow is the default here as it is in `select.tsx` and `menu.tsx`, and a
 * caller who says nothing gets an edge rather than a white panel floating on grey. Overridable
 * through `className` like the rest.
 *
 * Height is bounded here too: a panel taller than the viewport has no scrollbar of its own, so the
 * caller gives its body `overflow-y-auto` and this keeps the panel on screen.
 *
 * `Dialog.Root` supplies the rest of the contract this exists for — focus is trapped while open,
 * Escape closes, and focus returns to the trigger or, through `finalFocus`, wherever the caller
 * says. A dialog opened from a menu item must name that element: the item itself is unmounted
 * with the menu, and focus with nowhere to go falls to `<body>`.
 */
function DialogPopup({ className, children, ...props }: DialogPrimitive.Popup.Props) {
  return (
    <DialogPrimitive.Portal>
      {/* Drawn for a nested dialog too (Base UI leaves it out by default): a confirm opened over a
          dialog dims that dialog, so the question in front is the only thing that reads. */}
      <DialogPrimitive.Backdrop
        forceRender
        data-slot="dialog-backdrop"
        // The panel's own layer, not one under it: layers alike stack in the order they open, so a
        // nested dialog's scrim covers the dialog it opened from.
        className="fixed inset-0 z-50 bg-(--scrim) backdrop-blur-[2px]"
      />
      <DialogPrimitive.Popup
        {...props}
        data-slot="dialog-popup"
        className={merge(
          "fixed top-1/2 left-1/2 z-50 flex max-h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-clip rounded-xl border border-input bg-card text-foreground shadow-[0_24px_60px_#17171a29,0_3px_8px_#17171a14] outline-none",
          className,
        )}
      >
        {children}
      </DialogPrimitive.Popup>
    </DialogPrimitive.Portal>
  );
}

/**
 * The heading and the line under it, as ordinary utilities a caller can override. The package's
 * stylesheet resets only their margin, because Base UI renders them as `<h2>` and `<p>` into a
 * portal where a host app's own element rules can still reach them.
 */
function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      {...props}
      data-slot="dialog-title"
      className={merge("text-[19px]/6 font-semibold tracking-[-0.01em] text-foreground", className)}
    />
  );
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      {...props}
      data-slot="dialog-description"
      className={merge("text-[13px]/[19px] text-muted-foreground", className)}
    />
  );
}

/**
 * Holds every button inside it to the action size, whatever class a caller put on the button, so
 * a dialog cannot drift back to a field-height button. The selectors outrank a single utility.
 */
const ACTIONS =
  "[&_[data-slot=button]]:h-(--action-height) [&_[data-slot=button]]:gap-1.5 [&_[data-slot=button]]:rounded-[6px] [&_[data-slot=button]]:px-3 [&_[data-slot=button]]:text-[13px]";

/**
 * The band that ends a dialog whose body runs edge to edge: a hairline, the band ground, and the
 * actions on the right with the primary last. `note` sits on the left — a line about what
 * happens next, never a second question.
 */
function DialogFooter({
  className,
  note,
  children,
  ...props
}: ComponentProps<"div"> & { note?: ReactNode }) {
  return (
    <div
      {...props}
      data-slot="dialog-footer"
      className={cn(
        "flex shrink-0 flex-wrap items-center justify-end gap-x-2 gap-y-3 border-t border-divider bg-band px-6 py-3",
        ACTIONS,
        className,
      )}
    >
      {note && (
        <p className="m-0 mr-auto flex items-center gap-1.75 text-xs/[18px] text-muted-foreground">
          {note}
        </p>
      )}
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}

/** The same actions for a padded dialog with no band: a row on the right, primary last. */
function DialogActions({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      {...props}
      data-slot="dialog-actions"
      className={cn("flex flex-wrap items-center justify-end gap-2", ACTIONS, className)}
    />
  );
}

/**
 * The thing a confirm is about, shown so the eye checks what goes: a quiet chip with a small label
 * of who or what and when ("You said · 2:29"), and the thing itself. `media` puts a picture on its
 * left, such as a screen's thumbnail.
 */
function DialogObject({
  label,
  media,
  className,
  children,
}: {
  label?: ReactNode;
  media?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      data-slot="dialog-object"
      className={cn(
        "flex items-center gap-3 rounded-[8px] bg-secondary px-3 py-2.5",
        media && "py-2 pl-2",
        className,
      )}
    >
      {media}
      <div className="flex min-w-0 flex-col gap-1">
        {label && (
          <span className="text-[11px]/3.5 font-medium tracking-[0.02em] text-muted-foreground uppercase">
            {label}
          </span>
        )}
        <div
          className={cn("text-sm/5 text-body-foreground", media ? "line-clamp-2" : "line-clamp-3")}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Confirming what can't be undone, in one shape wherever it is asked (Paper "C · Alert dialog",
 * Ying, 1 November 2026). The title names the thing, `object` shows it, the description says in
 * one sentence what follows, and the footer says once that it can't be undone. Keep it (Esc) and
 * the yes, an outlined destructive button that says what it does ("Remove line"), end it.
 *
 * While `pending` the yes shows its loader and turns off, Keep it turns off, and the dialog stays
 * open: Escape and the scrim do nothing until the answer comes back. `error` sits above the
 * footer, and the yes stays live to try again.
 */
function DialogConfirm({
  open,
  onClose,
  title,
  object,
  children,
  note = "Can't be undone",
  keepLabel = "Keep it",
  confirmLabel,
  confirmIcon,
  pending = false,
  error,
  onConfirm,
}: {
  open: boolean;
  /** Asked to close: Keep it, Escape or the scrim. Not called while `pending`. */
  onClose: () => void;
  title: ReactNode;
  object?: ReactNode;
  /** What follows, in one sentence. */
  children: ReactNode;
  note?: ReactNode;
  keepLabel?: ReactNode;
  confirmLabel: ReactNode;
  confirmIcon?: ReactNode;
  pending?: boolean;
  error?: ReactNode;
  onConfirm: () => void;
}) {
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (next || pending) return;
        onClose();
      }}
    >
      <DialogPopup className="w-105">
        <div className="flex flex-col gap-3.5 px-5 pt-5 pb-4.5">
          <DialogTitle className="text-base/[22px]">{title}</DialogTitle>
          {object}
          <DialogDescription className="text-body-foreground">{children}</DialogDescription>
          {error}
        </div>
        <DialogFooter note={note} className="px-5">
          <DialogPrimitive.Close render={<Button variant="outline" disabled={pending} />}>
            {keepLabel}
            <Kbd aria-hidden="true">esc</Kbd>
          </DialogPrimitive.Close>
          <Button
            type="button"
            variant="destructive"
            className="border-destructive/35"
            pending={pending}
            onClick={onConfirm}
          >
            {!pending && confirmIcon}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </DialogPrimitive.Root>
  );
}

/** Supported Dialog parts only; Root keeps Base UI's own open-state and modality contract. */
export const Dialog = {
  Root: DialogPrimitive.Root,
  Popup: DialogPopup,
  Title: DialogTitle,
  Description: DialogDescription,
  Close: DialogPrimitive.Close,
  Footer: DialogFooter,
  Actions: DialogActions,
  Object: DialogObject,
  Confirm: DialogConfirm,
};
